import { useTranslationRef } from '@backstage/core-plugin-api/alpha';
import {
  GetNotificationsOptions,
  notificationsTranslationRef,
  useNotificationsApi,
} from '@backstage/plugin-notifications';
import {
  Notification,
  NotificationSeverity,
} from '@backstage/plugin-notifications-common';
import { useSignal } from '@backstage/plugin-signals-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

type View = 'all' | 'saved' | 'unread' | 'read';
type CreatedAfter = 'all' | 'last24h' | 'lastWeek';
const sorting = {
  newest: { sort: 'created', sortOrder: 'desc' },
  oldest: { sort: 'created', sortOrder: 'asc' },
  topic: { sort: 'topic', sortOrder: 'asc' },
  origin: { sort: 'origin', sortOrder: 'asc' },
} as const;

export type NotificationsFiltersState = {
  view: View;
  setView: (view: View) => void;
  createdAfter: CreatedAfter;
  setCreatedAfter: (value: CreatedAfter) => void;
  sortBy: keyof typeof sorting;
  setSortBy: (value: keyof typeof sorting) => void;
  severity: NotificationSeverity;
  setSeverity: (value: NotificationSeverity) => void;
  topic?: string;
  setTopic: (value: string | undefined) => void;
  allTopics?: string[];
};

// Keep the upstream page's leading/trailing two-second refresh throttle, without
// adding a dependency just to compose the existing public notifications APIs.
function useRefreshRequest() {
  const [refresh, setRefresh] = useState(false);
  const lastRefresh = useRef<number>();
  const timer = useRef<ReturnType<typeof setTimeout>>();
  const requestRefresh = useCallback(() => {
    const remaining =
      lastRefresh.current === undefined
        ? 0
        : 2000 - (Date.now() - lastRefresh.current);
    if (remaining <= 0) {
      clearTimeout(timer.current);
      timer.current = undefined;
      lastRefresh.current = Date.now();
      setRefresh(true);
    } else if (!timer.current) {
      timer.current = setTimeout(() => {
        timer.current = undefined;
        lastRefresh.current = Date.now();
        setRefresh(true);
      }, remaining);
    }
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);
  return { refresh, setRefresh, requestRefresh };
}

// Same queries, defaults, highlighted-notification handling and refresh behavior
// as the upstream page; the app owns composition so it can use Catalog filters.
export function useNotificationsPage() {
  const { t } = useTranslationRef(notificationsTranslationRef);
  const { lastSignal } = useSignal('notifications');
  const [searchParams] = useSearchParams();
  const highlightedNotificationId = searchParams.get('id') || undefined;
  const [highlightedNotification, setHighlightedNotification] =
    useState<Notification>();
  const [unreadOnly, setUnreadOnly] = useState<boolean | undefined>(true);
  const [saved, setSaved] = useState<boolean>();
  let view: View = 'all';
  if (saved) view = 'saved';
  else if (unreadOnly === true) view = 'unread';
  else if (unreadOnly === false) view = 'read';
  const setView = useCallback((nextView: View) => {
    setUnreadOnly(
      nextView === 'all' || nextView === 'saved'
        ? undefined
        : nextView === 'unread',
    );
    setSaved(nextView === 'saved' ? true : undefined);
  }, []);
  const [pageNumber, setPageNumber] = useState(0);
  const [pageSize, setPageSize] = useState(5);
  const [containsText, setContainsText] = useState<string>();
  const [createdAfter, setCreatedAfter] = useState<CreatedAfter>('all');
  const [sortBy, setSortBy] = useState<keyof typeof sorting>('newest');
  const [severity, setSeverity] = useState<NotificationSeverity>('low');
  const [topic, setTopic] = useState<string>();
  const { refresh, setRefresh, requestRefresh } = useRefreshRequest();

  const { error, value, retry, loading } = useNotificationsApi(
    api => {
      const options: GetNotificationsOptions = {
        search: containsText,
        limit: pageSize,
        offset: pageNumber * pageSize,
        minimumSeverity: severity,
        ...sorting[sortBy],
      };
      if (unreadOnly !== undefined) options.read = !unreadOnly;
      if (saved !== undefined) options.saved = saved;
      if (topic !== undefined) options.topic = topic;
      if (createdAfter !== 'all') {
        const days = createdAfter === 'last24h' ? 1 : 7;
        options.createdAfter = new Date(Date.now() - days * 24 * 3600 * 1000);
      }
      return Promise.all([
        api.getNotifications(options),
        api.getStatus(),
        api.getTopics(options),
      ]);
    },
    [
      containsText,
      unreadOnly,
      saved,
      createdAfter,
      pageNumber,
      pageSize,
      sortBy,
      severity,
      topic,
    ],
  );

  useEffect(() => {
    if (refresh && !loading) {
      retry();
      setRefresh(false);
    }
  }, [refresh, loading, retry, setRefresh]);
  useEffect(() => {
    if (lastSignal?.action) requestRefresh();
  }, [lastSignal, requestRefresh]);
  useEffect(() => {
    if (!highlightedNotificationId) {
      setHighlightedNotification(undefined);
      return;
    }
    setPageNumber(0);
  }, [highlightedNotificationId]);

  const { value: fetchedHighlightedNotification } = useNotificationsApi(
    api =>
      highlightedNotificationId
        ? api.getNotification(highlightedNotificationId)
        : Promise.resolve(undefined),
    [highlightedNotificationId],
  );
  useEffect(() => {
    if (fetchedHighlightedNotification) {
      setHighlightedNotification(fetchedHighlightedNotification);
      setUnreadOnly(!fetchedHighlightedNotification.read);
    }
  }, [fetchedHighlightedNotification]);

  const notifications = value?.[0].notifications;
  const injectedHighlightedNotification =
    highlightedNotification &&
    !notifications?.some(item => item.id === highlightedNotification.id);
  const displayedNotifications = useMemo(
    () =>
      injectedHighlightedNotification
        ? [highlightedNotification, ...(notifications ?? [])]
        : notifications,
    [injectedHighlightedNotification, highlightedNotification, notifications],
  );
  const totalCount = value?.[0].totalCount;

  return {
    error,
    filters: {
      view,
      setView,
      createdAfter,
      setCreatedAfter,
      sortBy,
      setSortBy,
      severity,
      setSeverity,
      topic,
      setTopic,
      allTopics: value?.[2].topics,
    },
    table: {
      title: t(`notificationsPage.tableTitle.${view}`, {
        count: totalCount ?? 0,
      }),
      isLoading: loading,
      isUnread: !!value?.[1].unread,
      notifications: displayedNotifications,
      highlightedNotificationId,
      onUpdate: requestRefresh,
      setContainsText,
      onPageChange: setPageNumber,
      onRowsPerPageChange: setPageSize,
      page: pageNumber,
      pageSize: injectedHighlightedNotification ? pageSize + 1 : pageSize,
      totalCount,
    },
  };
}
